import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { useTranslation } from 'react-i18next';
import { Tabs } from '../../ui/Tabs/Tabs';
import { EmailTab } from './EmailTab';
// import { ManualInterventionTab } from './ManualInterventionTab';
import { Dialogue } from '../../components/Dialogue/Dialogue';
import { CoordinatesBadge } from './CoordinatesBadge';
import { CoordinatesBadgeSkeleton } from './CoordinatesBadgeSkeleton';
import { FollowUpRecapActionsSkeleton } from './FollowUpRecapActionsSkeleton';
import { SyncFreshnessLine } from '../../components/SyncFreshnessLine/SyncFreshnessLine';
import { PageTitle } from '../../ui/PageTitle/PageTitle';
import { TranslationKey } from '../../i18n/translationKeys';
import { relancesService } from '../../services/relancesService';
import { tasksService } from '../../services/tasksService';
import { StatementSentNote } from '../../features/relance/StatementSentNote';
import { MissingInformationBanner } from '../../features/relance/MissingInformationBanner';
import { hasMissingMandatoryInformation } from '../../features/relance/missingInformation';
import { usePartialSync } from '../../hooks/usePartialSync';
import { ReadScope } from '../../types/sync/enums';
import { AiTextRenderer } from '../../components/AITextRenderer/AiTextRenderer';
import { AiSparkle } from '../../components/AiSparkle/AiSparkle';
import { TypewriterText } from '../../ui/TypewriterText/TypewriterText';
import { HttpError } from '../../services/apiClient';
import { localizeApiError } from '../../utils/apiErrors';
import { HistoryTimeline } from '../History/HistoryTimeline';
import { useTasksStore } from '../../stores/tasksStore';
import { useLanguageStore } from '../../stores/languageStore';
import { useNoAiStore } from '../../stores/noAiStore';
import { useToastStore } from '../../stores/toastStore';
import { useSyncStore } from '../../stores/syncStore';
import { useAcombaBridgeStore } from '../../stores/acombaBridgeStore';
import { RelanceStage, type RelanceDto, type RelanceFlagsDto } from '../../types/customerFollowUp/relance';
import { getSyncPhaseTranslationKey, isPhaseBefore } from '../../utils/sync/syncPhase';
import { SyncPhase } from '../../types/sync/enums';
import { useInFlightPoll } from './useInFlightPoll';
import { useNextTask } from '../../hooks/useNextTask';
import { formatCurrency, formatLanguageDisplay } from '../../utils/formatters';
import { isRelanceListed } from '../../utils/relance/isRelanceListed';
import { FollowUpRecapActions } from '../../features/relance/FollowUpRecapActions';
import { FollowUpNote } from '../../features/relance/FollowUpNote';
import { RelanceRankTag } from '../../features/relance/RelanceRankTag';
import { SentRelancesDropdown } from '../../features/relance/SentRelancesDropdown';
import { ReceivablesRowSkeleton } from '../../features/kpi/ReceivablesRowSkeleton';
import { FollowUpFigures } from './FollowUpFigures';
import type { IgnoreRelanceSubmission } from '../../features/relance/IgnoreRelanceModal';
import { useRelanceDeactivationStore } from '../../stores/relanceDeactivationStore';
import { useCanCollectPayment } from '../../hooks/useCanCollectPayment';
import { featureFlags } from '../../utils/featureFlags';
import { SkeletonBar } from '../../ui/Skeleton/SkeletonBar';
import { Banner } from '../../ui/Banner/Banner';

// Queued → Generating → Recommendation → Drafting: the four RelanceStage values a run passes through.
const GENERATION_STAGE_COUNT = 4;

export const CustomerFollowUp: React.FC = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const appNavigate = useAppNavigate();
    const { markTaskCompleted } = useTasksStore();
    const fetchDailyTasks = useTasksStore(s => s.fetchDailyTasks);
    const completedTaskIds = useTasksStore(s => s.completedTaskIds);
    const { getNextRoute } = useNextTask();
    const lang = useLanguageStore(s => s.lang);
    const noAi = useNoAiStore(s => s.noAi);
    const showToast = useToastStore(s => s.showToast);
    const deactivateTask = useRelanceDeactivationStore(s => s.deactivateTask);
    const canCollectPayment = useCanCollectPayment();
    const refreshLastSyncedAt = useSyncStore(s => s.refreshLastSyncedAt);
    const [followUp, setFollowUp] = useState<RelanceDto | null>(null);
    const [flags, setFlags] = useState<RelanceFlagsDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [recapTitle, setRecapTitle] = useState('');
    const [recapReason, setRecapReason] = useState('');
    // Only text that keeps arriving types in; a one-chunk cache hit renders whole.
    const [recapTitleLive, setRecapTitleLive] = useState(false);
    const [recapReasonLive, setRecapReasonLive] = useState(false);
    const [recapStreaming, setRecapStreaming] = useState(false);
    const [recapError, setRecapError] = useState<string | null>(null);
    const recapAbortRef = useRef<AbortController | null>(null);
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [sendBarSlot, setSendBarSlot] = useState<HTMLDivElement | null>(null);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [historyCount, setHistoryCount] = useState<number | null>(null);
    // Bumped when a sync finishes, to re-read the relance and its flags. Not a boolean: the agent can
    // refresh twice, and a flag would only fire the first re-read.
    const [reloadToken, setReloadToken] = useState(0);
    const wasSyncingRef = useRef(false);
    // Which id the page has already loaded. The loading skeleton shows only on the first load of an id; a
    // re-read (a reloadToken bump after a sync settles) keeps the page mounted so a half-written draft in
    // EmailTab survives. See the fetch effect.
    const loadedIdRef = useRef<string | null>(null);
    // The id a settled sync asked to re-read. `reloadToken` is a bare counter that survives an id swap
    // ("Envoyer et suivant" changes the route id without unmounting), so on its own it cannot tell "this
    // relance was refreshed" from "some earlier one was". Scoping the post-refresh handling to this id is
    // what does: a 404 or a working-list miss counts as "removed by a sync" only for the id we refreshed.
    const refreshedIdRef = useRef<string | null>(null);
    // The two ways a refresh can reveal the relance is gone — a 404 on the re-read, and an absence from
    // the re-read working list — are checked by separate effects that both react to the same reloadToken
    // bump. A deleted relance trips both, so without this the agent gets the toast twice. Whichever
    // notices first redirects; the other stands down. Re-armed on the next reload (see the fetch effect).
    const leftWorkingListRef = useRef(false);

    // Sampled once per id: a send from this page marks it completed a render before the route moves on.
    const [arrival, setArrival] = useState(() => ({ id, completed: !!id && completedTaskIds.includes(id) }));
    if (arrival.id !== id) setArrival({ id, completed: !!id && completedTaskIds.includes(id) });
    // sentAt stays live, unlike the session mark: a send from another tab or device must pull the composer.
    const alreadySent = !!followUp?.sentAt || arrival.completed;

    // Per relance, not tenant-wide: a relance can still be generating after the sync's veil lifts.
    const stage = followUp?.stage ?? null;
    const inFlight = stage === RelanceStage.Queued || stage === RelanceStage.Generating
        || stage === RelanceStage.Recommendation || stage === RelanceStage.Drafting;
    // No recommendation row exists yet while Queued/Generating, and before the first load the stage is
    // unknown rather than idle, so the recap stream waits for both.
    const recapReady = followUp !== null && stage !== RelanceStage.Queued && stage !== RelanceStage.Generating;

    const hasRecapText = !!recapTitle || !!recapReason;
    // `!recapStreaming`: a failed or deferred relance may still hold last sync's text, and only the settled
    // fetch knows. `!recapError`: a fetch failure keeps its own message.
    const noTextToShow = !inFlight && !hasRecapText && !recapStreaming && !recapError;
    // Once per id, and only after the first load: the pre-load defaults read as "never generated" for every relance.
    // State, not a ref: the render below reads it, and marking the id must re-render.
    const [autoGeneratedId, setAutoGeneratedId] = useState<string | null>(null);
    const [generateRequested, setGenerateRequested] = useState(false);
    const shouldAutoGenerate = followUp !== null && !alreadySent && (inFlight || noTextToShow);
    // Only an outstanding generate call counts, plus the render before the effect fires it.
    const generatePending = generateRequested || (shouldAutoGenerate && autoGeneratedId !== id);
    const isGenerating = inFlight || generatePending;
    // Earlier-sync text keeps showing while a new run is in flight, so this is only for a relance with none.
    const showPendingPlaceholder = isGenerating && !hasRecapText;
    // A relance is generating, ready or failed. Once generation is neither running nor pending and left no
    // text (a Failed run, no report to generate from, a failed request), it reads as failed.
    const showFailedPlaceholder = noTextToShow && !isGenerating;
    // The email tab regenerates its body on its first unlocked render, so it stays locked until the
    // relance's state is known rather than regenerating a body a run is about to replace.
    const generationStateKnown = followUp !== null && (inFlight || !recapStreaming);
    // Replaces "Last updated…" in the freshness line while a run is in flight, stepping through the stages
    // in the order the backend reports them.
    const total = GENERATION_STAGE_COUNT;
    const generationStatusLabel = !isGenerating ? null
        : stage === RelanceStage.Generating ? t(TranslationKey.CustomerFollowUp.GenerationStatus.Generating, { step: 2, total })
        : stage === RelanceStage.Recommendation ? t(TranslationKey.CustomerFollowUp.GenerationStatus.PlanningEmail, { step: 3, total })
        : stage === RelanceStage.Drafting ? t(TranslationKey.CustomerFollowUp.GenerationStatus.GeneratingEmail, { step: 4, total })
        : t(TranslationKey.CustomerFollowUp.GenerationStatus.Queued, { step: 1, total });

    // Tracked during render, not in an effect, so the recap effect below sees it on the same render. Text
    // the user watched arrive must type in even though the recap endpoint serves it as a cache hit.
    const stageInFlight = stage === RelanceStage.Queued || stage === RelanceStage.Generating;
    const prevStageInFlightRef = useRef(stageInFlight);
    const arrivedWhileWatchingRef = useRef(false);
    if (prevStageInFlightRef.current !== stageInFlight) {
        arrivedWhileWatchingRef.current = prevStageInFlightRef.current && !stageInFlight;
        prevStageInFlightRef.current = stageInFlight;
    }

    const returnToWorkingList = () => {
        if (leftWorkingListRef.current) return;
        leftWorkingListRef.current = true;
        showToast('error', t(TranslationKey.CustomerFollowUp.NoLongerAvailable));
        void navigate('/', { replace: true });
    };

    const completeAndDelete = useCallback((taskId: string) => {
        markTaskCompleted(taskId);
    }, [markTaskCompleted]);

    const handleSend = useCallback(() => {
        if (!id) return;
        completeAndDelete(id);
        appNavigate('/');
    }, [id, completeAndDelete, appNavigate]);

    const handleDeactivate = useCallback(async (submission: IgnoreRelanceSubmission): Promise<boolean> => {
        if (!id) return false;
        const done = await deactivateTask(id, submission);
        if (!done) {
            showToast('error', t(TranslationKey.ApiErrors.Generic));
            return false;
        }
        appNavigate('/');
        return true;
    }, [id, deactivateTask, appNavigate, showToast, t]);

    const handleSendAndNext = useCallback(() => {
        if (!id) return;
        completeAndDelete(id);
        // Replaced, not pushed: going back must not reopen the relance that was just sent.
        const next = getNextRoute(id);
        appNavigate(next, next === '/' ? undefined : { replace: true });
    }, [id, completeAndDelete, appNavigate, getNextRoute]);

    const { syncing: isSyncing, progress: syncProgress, trigger: triggerSync } = usePartialSync(ReadScope.TargetedRefresh);

    // Figures are final once the data pull ends and the fill only writes AI text, so the veil lifts at
    // RelanceFill. The refresh lock stays on raw `isSyncing`.
    const isVeiled = isSyncing && isPhaseBefore(syncProgress?.phase, SyncPhase.RelanceFill);
    // The stage loaded before the sync is stale once the veil lifts: this relance may have just been
    // dispatched. The page stays sealed until it is re-read, so the email tab never unlocks on it.
    const [awaitingStageRead, setAwaitingStageRead] = useState(false);
    const wasVeiledRef = useRef(false);
    const isPageVeiled = isVeiled || awaitingStageRead;

    // Drives the label the banner shows next to its spinner while a re-sync runs. The Acomba re-read
    // happens in the client before anything reaches the backend, so there is no phase to report for it —
    // name the extraction instead of implying a backend step. One customer is being re-read here, so the
    // per-entity ExtractionProgress block would be noise; the heading alone carries it.
    const isExtracting = useAcombaBridgeStore(s => s.isExtracting);
    const syncPhaseKey = getSyncPhaseTranslationKey(syncProgress?.phase);
    const syncPhaseLabel = isExtracting
        ? t(TranslationKey.AcombaBridge.Extraction.Heading)
        : syncPhaseKey ? t(syncPhaseKey) : null;

    // The user fixed the client info in AcombaGO; a partial re-sync picks up that customer
    // (changed since the last sync) and recomputes its flags. Reload once the sync resolves so
    // the banner re-evaluates against the fresh data.
    const handleRetrySync = useCallback(async () => {
        try {
            await triggerSync();
            window.location.reload();
        } catch {
            // surface the error via setError or showToast
        }
    }, [triggerSync]);

    // No sync dispatches a deferred relance, so this is the only path that starts one.
    const handleGenerate = useCallback(async () => {
        if (!id) return;
        setGenerateRequested(true);
        try {
            await relancesService.generate(id, noAi);
            const relance = await relancesService.getRelance(id);
            setFollowUp(relance);
        } catch (e) {
            // 404: no report rendered yet to generate from. The page shows it as failed; no toast on top.
            if (e instanceof HttpError && e.status === 404) return;
            showToast('error', localizeApiError(e, t));
        } finally {
            setGenerateRequested(false);
        }
    }, [id, noAi, showToast, t]);

    // The backend decides what "generate" means for each stage, so the page never arbitrates.
    useEffect(() => {
        if (!id || !shouldAutoGenerate || autoGeneratedId === id) return;
        setAutoGeneratedId(id);
        void handleGenerate();
    }, [id, shouldAutoGenerate, autoGeneratedId, handleGenerate]);

    useEffect(() => {
        if (!id) return;
        // "Envoyer et suivant" swaps the route id without unmounting this page, so reset the history
        // count here — otherwise the "Historique du client (N)" badge would keep the previous client's count
        // until the new HistoryTimeline reports one. Falls back to (0) via `historyCount ?? 0`.
        setHistoryCount(null);

        // Re-arm the single-redirect guard for this reload cycle. Safe to do here: both consumers act
        // from an async callback, so neither can have fired by the time this effect body runs.
        leftWorkingListRef.current = false;

        // Skeleton only on the first load of an id. A re-read (reloadToken bump after a sync settles) keeps
        // the page mounted so EmailTab — and any half-written draft it holds — survives, with the fresh data
        // landing underneath it. Flipping to the skeleton here would unmount the tab and lose the draft,
        // which is the whole reason the sync uses a veil rather than Home's swap-for-a-progress-screen.
        if (loadedIdRef.current !== id) setLoading(true);
        setError(null);
        relancesService.getRelance(id)
            .then(r => { loadedIdRef.current = id; setFollowUp(r); })
            .catch((e: Error) => {
                if (e instanceof HttpError && e.status === 404) {
                    // A 404 on first load is a bad link — the not-found page says so. After a refresh we
                    // triggered ourselves it means something else: the sync removed this relance while the
                    // agent was looking at it. Sending them to a not-found page would read as a broken app,
                    // so say what happened and put them back on the list.
                    if (refreshedIdRef.current === id) {
                        returnToWorkingList();
                    } else {
                        void navigate('/not-found', { replace: true });
                    }
                } else {
                    setError(localizeApiError(e, t));
                }
            })
            .finally(() => setLoading(false));
        // reloadToken is a dependency on purpose: bumping it is how a finished sync asks for a re-read.
        // `t`, `showToast` and `returnToWorkingList` (which closes over both) are deliberately absent:
        // `t` gets a fresh identity on most renders, so listing them would re-fetch the relance on every
        // unrelated re-render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, navigate, reloadToken]);

    // Missing-information flags live on the task DTO (not the relance detail DTO), so they
    // are fetched separately. A failure here only hides the banner — it must not block the page.
    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        tasksService.getTaskById(id)
            .then(task => { if (!cancelled) setFlags(task.flags); })
            .catch(() => { if (!cancelled) setFlags(null); });
        return () => { cancelled = true; };
    }, [id, reloadToken]);

    // A refresh from the freshness line syncs the whole tenant, so this relance's figures and flags may
    // have moved. Re-read them when the sync settles — the same true→false transition Home watches to
    // reload its lists. Without this the agent would refresh and keep looking at the old amounts.
    useEffect(() => {
        if (wasSyncingRef.current && !isSyncing) {
            refreshedIdRef.current = id ?? null;
            setReloadToken(n => n + 1);
        }
        wasSyncingRef.current = isSyncing;
    }, [id, isSyncing]);

    // Only for a veil lifting mid-sync; when the sync itself ends, the reloadToken re-read above covers it.
    useEffect(() => {
        const lifted = wasVeiledRef.current && !isVeiled;
        wasVeiledRef.current = isVeiled;
        if (!lifted || !isSyncing || !id) return;
        let cancelled = false;
        setAwaitingStageRead(true);
        relancesService.getRelance(id)
            .then(r => { if (!cancelled) setFollowUp(r); })
            .catch(() => undefined)
            .finally(() => { if (!cancelled) setAwaitingStageRead(false); });
        return () => { cancelled = true; setAwaitingStageRead(false); };
    }, [id, isVeiled, isSyncing]);

    // The freshness line reads `lastSyncedAt` from an in-memory store, so a direct load of this page — a
    // browser refresh, a deep link, or `handleRetrySync`'s `window.location.reload()` — starts with none.
    // Home seeds it on its own mount; nothing else does here, so without this the date and its Rafraîchir
    // action would be absent on a standalone load. Seed it here too, exactly as Home does.
    useEffect(() => {
        void refreshLastSyncedAt();
    }, [refreshLastSyncedAt]);

    // A sync can take a relance out of the working list without deleting it: the customer paying deletes
    // the task row while the relance row survives, so the re-read above still answers 200. Membership
    // lives in the daily list, so that is what has to be checked — and re-reading it leaves the shared
    // store correct for when the agent goes back to Home.
    //
    // Fail-open: only a list we actually read decides this. If the fetch failed, `error` is set and we
    // leave the agent where they are rather than evicting them over a network blip.
    useEffect(() => {
        if (!id || refreshedIdRef.current !== id) return;
        let cancelled = false;

        void (async () => {
            // The freshness line reads `lastSyncedAt` from the store and does not refresh it itself — the
            // page owns that, so that the date and the content it sits above always move together. Home
            // does the same in its own post-sync reload; without this the label kept the old time until the
            // agent went back to Home.
            await Promise.all([fetchDailyTasks(), refreshLastSyncedAt()]);
            if (cancelled) return;

            const { dailyTasks, error: listError } = useTasksStore.getState();
            if (listError || !dailyTasks || isRelanceListed(dailyTasks, id)) return;

            returnToWorkingList();
        })();

        return () => { cancelled = true; };
        // Same reason as the fetch above: `t`, `showToast` and `returnToWorkingList` would re-run this on
        // unrelated renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reloadToken, id, navigate, fetchDailyTasks, refreshLastSyncedAt]);

    // Resets on an id change only, so a relance with a new run in flight keeps showing last sync's text.
    useEffect(() => {
        if (!id) return;
        recapAbortRef.current?.abort();
        setRecapTitle('');
        setRecapReason('');
        setRecapTitleLive(false);
        setRecapReasonLive(false);
        // True until the fetch below settles: "no text yet" means "still finding out", and the failed
        // placeholder waits for false.
        setRecapStreaming(true);
        setRecapError(null);
        prevStageInFlightRef.current = false;
        arrivedWhileWatchingRef.current = false;
    }, [id]);

    useEffect(() => {
        if (!id || !recapReady) return;
        recapAbortRef.current?.abort();
        const controller = new AbortController();
        recapAbortRef.current = controller;

        // Consumed once, so a plain reload of an already-settled stage does not re-type its text.
        const arrivedWhileWatching = arrivedWhileWatchingRef.current;
        arrivedWhileWatchingRef.current = false;

        setRecapTitleLive(arrivedWhileWatching);
        setRecapReasonLive(arrivedWhileWatching);
        setRecapStreaming(true);
        setRecapError(null);

        // A second chunk means the field is still streaming (a translation miss), so it types in from there.
        const streamField = async (field: 'title' | 'reason', set: (v: string) => void, setLive: (v: boolean) => void) => {
            let acc = '';
            let chunkCount = 0;
            for await (const chunk of relancesService.streamRecommendationField(id, field, lang, noAi, controller.signal)) {
                chunkCount += 1;
                acc += chunk;
                set(acc);
                if (chunkCount > 1) setLive(true);
            }
        };

        void Promise.all([
            featureFlags.aiRecapTitle ? streamField('title', setRecapTitle, setRecapTitleLive) : Promise.resolve(),
            streamField('reason', setRecapReason, setRecapReasonLive),
        ])
            .catch((e: Error) => {
                if (e.name !== 'AbortError') setRecapError(t(TranslationKey.CustomerFollowUp.Recap.Error));
            })
            .finally(() => setRecapStreaming(false));

        return () => controller.abort();
    }, [id, lang, noAi, t, recapReady]);

    // Nothing pushes stage changes to this page, so it polls while the relance is in flight.
    useInFlightPoll(inFlight, () => {
        if (!id) return Promise.resolve();
        return relancesService.getRelance(id).then(setFollowUp);
    });

    if (!id) return null;

    if (error) {
        return (
            <div className="p-xl">
                <Dialogue error={error} />
            </div>
        );
    }

    const tabs = [
        {
            testId: 'followup-tab-email',
            label: t(TranslationKey.CustomerFollowUp.Tabs.Email),
            content: alreadySent ? (
                <Banner tone="info" testId="followup-already-sent">
                    {t(TranslationKey.CustomerFollowUp.AlreadySent)}
                </Banner>
            ) : (
                <div className="flex flex-col gap-l">
                    <MissingInformationBanner flags={flags} customerName={followUp?.customerName ?? ''} customerExternalId={followUp?.customerExternalId ?? null} onRetry={handleRetrySync} isSyncing={isPageVeiled} syncPhaseLabel={syncPhaseLabel} />
                    {/* The route id can swap while this page stays mounted, and the composer holds state no
                        prop change resets — the ticked consent box, the per-tone body cache. The key is
                        what clears them. */}
                    <EmailTab key={id} relanceId={id} onSend={handleSend} onSendAndNext={handleSendAndNext} hasMissingMandatoryInformation={hasMissingMandatoryInformation(flags)} isSyncing={isPageVeiled} inFlight={isGenerating || !generationStateKnown} runInFlight={inFlight} onSendingChange={setIsSendingEmail} paymentLinkCount={followUp?.paymentLinkCount ?? null} sendBarContainer={sendBarSlot} />
                </div>
            ),
        },
        {
            testId: 'followup-tab-history',
            label: (
                <>
                    {t(TranslationKey.CustomerFollowUp.Tabs.SuiviClient)}
                    <span
                        className="ml-xs"
                        aria-label={t(TranslationKey.CustomerFollowUp.Tabs.HistoryCountAriaLabel, { count: historyCount ?? 0 })}
                        data-testid="followup-tab-history-badge"
                    >
                        ({historyCount ?? 0})
                    </span>
                </>
            ),
            content: followUp?.customerId
                ? <HistoryTimeline customerId={followUp.customerId} onCountChange={setHistoryCount} groupBy="year" />
                : null,
        },
    ];

    const isLocked = isPageVeiled || isSendingEmail;

    const languageDisplay = formatLanguageDisplay(followUp?.language);
    const nameWithLanguage = [
        followUp?.contactName, 
        languageDisplay ? `(${languageDisplay})` : null
    ].filter(Boolean).join(' ');

    const contactLine1 = [
        nameWithLanguage,
        followUp?.contactPhone,
    ].filter(Boolean).join(' • ');

    const contactCityLine = [
        followUp?.contactPostalCode,
        followUp?.contactCity,
        followUp?.contactCountryReference,
    ].filter(Boolean).join(', ');

    // Contact data comes from the relance itself, so it shows whatever state the AI text is in.
    const coordinates = followUp && !loading
        ? [contactLine1, followUp.contactAddress, contactCityLine].filter(Boolean).join(', ')
        : '';

    return (
        <div className="flex flex-col gap-l">
            {/* Same placement as Home: its own line under the heading, never beside it. */}
            <div className="flex flex-col gap-m">
                {loading
                    ? <SkeletonBar className="h-5.5 w-56 max-w-full" />
                    : (
                        <div className="flex items-center gap-s min-w-0">
                            <PageTitle data-testid="followup-customer-name">{followUp?.customerName ?? '…'}</PageTitle>
                            {followUp?.relanceCount != null && (
                                <RelanceRankTag
                                    rank={followUp.relanceCount + 1}
                                    sent={!!followUp.sentAt}
                                    testId="followup-relance-rank"
                                />
                            )}
                        </div>
                    )}
                <SyncFreshnessLine
                    testId="followup-last-sync"
                    refreshTestId="followup-last-sync-refresh"
                    statusOverride={generationStatusLabel ?? undefined}
                />
            </div>

            {/* Everything below the freshness line is sealed off while a sync runs, and again while a send is
                in flight. `inert` is what does the work — it blocks clicks, keyboard focus and Enter-key
                submits, and drops the subtree from the accessibility tree; `pointer-events-none` alone would
                still let a focused input take typing. The opacity is only the visible cue.
                Nothing unmounts, so a half-written email survives the refresh — the reason this is a veil
                rather than Home's swap-for-a-progress-screen. */}
            {/* `inert` hides the send button's spinner from screen readers, so announce the send out here. */}
            {isSendingEmail && (
                <span role="status" data-testid="followup-sending-status" className="sr-only">
                    {t(TranslationKey.Common.Loading)}
                </span>
            )}
            <div
                inert={isLocked}
                data-testid="followup-sync-veil"
                className={`flex flex-col gap-3xl transition-opacity${isLocked ? ' opacity-50' : ''}`}
            >
                <div className="flex flex-col gap-l">
                    {loading ? <CoordinatesBadgeSkeleton /> : coordinates && <CoordinatesBadge coordinates={coordinates} />}
                    <div className="flex flex-col gap-s">
                        <h2 data-testid="followup-recap-heading" className="flex items-center gap-s text-body-emphasized font-semibold text-content">
                            <AiSparkle testId="followup-recap-sparkle" />
                            {t(TranslationKey.CustomerFollowUp.Recap.Heading)}
                        </h2>
                        {followUp?.latestNote && !loading && <FollowUpNote note={followUp.latestNote} />}
                        {showPendingPlaceholder ? (
                            <div data-testid="followup-recap-pending" role="status" aria-busy="true" className="flex flex-col gap-s">
                                {['', '', '', 'w-2/3'].map((width, i) => (
                                    <SkeletonBar key={i} className={`h-4 ${width}`} testId="followup-recap-pending-line" />
                                ))}
                            </div>
                        ) : showFailedPlaceholder ? (
                            <Dialogue error={t(TranslationKey.CustomerFollowUp.Pending.Failed)} errorTestId="followup-recap-error" />
                        ) : (
                            <>
                                {featureFlags.aiRecapTitle && !recapError && recapTitle && (
                                    <p data-testid="followup-recap-title" className="text-body-emphasized text-content">
                                        <TypewriterText text={recapTitle} speed={recapTitleLive ? 'live' : 'instant'}>
                                            {visible => <AiTextRenderer text={visible} bold={false} />}
                                        </TypewriterText>
                                    </p>
                                )}
                                <div data-testid="followup-recap-body" className="flex max-w-dialogue flex-col gap-s text-body font-normal text-content">
                                    <Dialogue
                                        loading={recapStreaming && !recapReason}
                                        error={recapError}
                                        errorTestId="followup-recap-error"
                                        skeleton={
                                            <div data-testid="followup-recap-skeleton" className="flex flex-col gap-s">
                                                {['', '', '', 'w-2/3'].map((width, i) => (
                                                    <SkeletonBar key={i} className={`h-4 ${width}`} testId="followup-recap-skeleton-line" />
                                                ))}
                                            </div>
                                        }
                                    >
                                        <TypewriterText text={recapReason} speed={recapReasonLive ? 'live' : 'instant'}>
                                            {visible => (visible ? visible.split('\n') : []).map((line, i) => (
                                                <p key={i}>
                                                    {/* The name already heads the page, so it is not repeated in bold here. */}
                                                    <AiTextRenderer text={line} unboldTerms={followUp ? [followUp.customerName] : []} />
                                                </p>
                                            ))}
                                        </TypewriterText>
                                    </Dialogue>
                                </div>
                            </>
                        )}
                    </div>
                    <StatementSentNote sentOn={followUp?.statementSentOn} dated />
                    {loading && <ReceivablesRowSkeleton count={2} testId="followup-figures-skeleton" />}
                    {followUp && !loading && (
                        <FollowUpFigures receivablesTotal={followUp.receivablesTotal} totalDue={followUp.totalDue} lang={lang} />
                    )}
                    {followUp && !loading && <SentRelancesDropdown customerId={followUp.customerId} />}
                    {loading && <FollowUpRecapActionsSkeleton />}
                    {followUp && !loading && (
                        <FollowUpRecapActions
                            relanceId={id}
                            customerName={followUp.customerName}
                            customerExternalId={followUp.customerExternalId}
                            totalDue={followUp.totalDue}
                            formattedTotalDue={formatCurrency(followUp.totalDue, lang)}
                            showPaidAction={canCollectPayment}
                            onDeactivate={handleDeactivate}
                        />
                    )}
                </div>
                <Tabs
                    tabs={tabs}
                    keepMounted
                    activeIndex={activeTabIndex}
                    onTabChange={setActiveTabIndex}
                />
            </div>
            {/* A sticky element stays inside its parent, so the email's send bar is hosted here, at the end of
                the whole page, to be in view from the top rather than only once the email scrolls up. */}
            {activeTabIndex === 0 && <div ref={setSendBarSlot} inert={isLocked} className={`sticky bottom-0 z-10 empty:hidden transition-opacity${isLocked ? ' opacity-50' : ''}`} />}
        </div>
    );
};
